import { useState, useEffect, useRef } from "react";
import { useDataChannel, useLocalParticipant, useParticipants } from "@livekit/components-react";

const CHAT_TOPIC = "ephemeral-chat";

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
}

type ChatPayload = 
  | { type: "msg"; message: ChatMessage }
  | { type: "sync"; messages: ChatMessage[] };

export const ChatWidget = ({ displayName }: { displayName?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");

  const { send, message } = useDataChannel(CHAT_TOPIC);
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const scrollRef = useRef<HTMLDivElement>(null);

  // --- НАШ ПАТЧ: ЗАЩИТА ОТ ФАНТОМНЫХ СООБЩЕНИЙ ---
  const lastProcessedMsgId = useRef<string | null>(null);

  const myId = localParticipant.identity || "unknown";
  const myName = displayName || localParticipant.name || "Гость";

  useEffect(() => {
	if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  useEffect(() => {
	if (scrollRef.current && isOpen) {
	  scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
	}
  }, [messages, isOpen]);

  // 1. Прием сообщений
  useEffect(() => {
	if (message && message.topic === CHAT_TOPIC) {
	  const decoder = new TextDecoder();
	  try {
		const data: ChatPayload = JSON.parse(decoder.decode(message.payload));

		if (data.type === "msg") {
		  // ЕСЛИ МЫ УЖЕ СЧИТАЛИ ЭТО СООБЩЕНИЕ - ИГНОРИРУЕМ
		  if (lastProcessedMsgId.current === data.message.id) return;
		  lastProcessedMsgId.current = data.message.id;

		  setMessages((prev) => {
			if (prev.find((m) => m.id === data.message.id)) return prev;
			return [...prev, data.message].sort((a, b) => a.timestamp - b.timestamp);
		  });
		  if (!isOpen) setUnreadCount((c) => c + 1);
		} else if (data.type === "sync") {
		  setMessages((prev) => {
			const merged = [...prev];
			let changed = false;
			data.messages.forEach((msg) => {
			  if (!merged.find((m) => m.id === msg.id)) {
				merged.push(msg);
				changed = true;
			  }
			});
			return changed ? merged.sort((a, b) => a.timestamp - b.timestamp) : prev;
		  });
		}
	  } catch (e) {
		console.error("Ошибка парсинга чата:", e);
	  }
	}
  }, [message, isOpen]);

  // 2. Раздача истории новичкам
  const prevParticipantsCount = useRef(participants.length);
  useEffect(() => {
	if (participants.length > prevParticipantsCount.current && messagesRef.current.length > 0) {
	  const encoder = new TextEncoder();
	  const payload = encoder.encode(JSON.stringify({ type: "sync", messages: messagesRef.current }));
	  send(payload, { reliable: true }).catch(console.error);
	}
	prevParticipantsCount.current = participants.length;
  }, [participants.length, send]);

  const handleSend = async (e: React.FormEvent) => {
	e.preventDefault();
	if (!inputText.trim()) return;

	const newMsg: ChatMessage = {
	  id: Math.random().toString(36).substring(2, 10),
	  senderId: myId,
	  senderName: myName,
	  text: inputText.trim(),
	  timestamp: Date.now(),
	};

	const encoder = new TextEncoder();
	const payload = encoder.encode(JSON.stringify({ type: "msg", message: newMsg }));
	
	await send(payload, { reliable: true });

	setMessages((prev) => [...prev, newMsg]);
	setInputText("");
  };

  return (
	<>
	  <button 
		onClick={() => setIsOpen(!isOpen)}
		style={{
		  position: "absolute", bottom: "100px", right: "20px", zIndex: 9999,
		  width: "56px", height: "56px", borderRadius: "50%",
		  backgroundColor: "rgba(30, 30, 34, 0.9)", border: "1px solid rgba(255,255,255,0.1)",
		  cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
		  display: isOpen ? "none" : "flex", justifyContent: "center", alignItems: "center",
		  transition: "transform 0.2s, background 0.2s", backdropFilter: "blur(8px)"
		}}
		onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.05)"}
		onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
	  >
		<svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
		  <path d="M21 11.5C21 16.75 16.5 21 12 21C10.5 21 9 20.5 7.5 19.5L3 21L4.5 16.5C3.5 15 3 13.5 3 11.5C3 6.25 7.5 2 12 2C16.5 2 21 6.25 21 11.5Z" />
		</svg>
		{unreadCount > 0 && (
		  <span style={{
			position: "absolute", top: "0", right: "0", backgroundColor: "#ff3b30", color: "white",
			fontSize: "12px", fontWeight: "bold", height: "20px", minWidth: "20px", borderRadius: "10px",
			display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px",
			boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
		  }}>
			{unreadCount > 99 ? "99+" : unreadCount}
		  </span>
		)}
	  </button>

	  <div style={{
		position: "absolute", top: 0, right: isOpen ? 0 : "-380px", bottom: "80px", width: "350px",
		backgroundColor: "rgba(30, 30, 34, 0.95)", backdropFilter: "blur(16px)",
		borderLeft: "1px solid rgba(255,255,255,0.1)", zIndex: 9998,
		display: "flex", flexDirection: "column", transition: "right 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
		boxShadow: isOpen ? "-8px 0 24px rgba(0,0,0,0.5)" : "none"
	  }}>
		<div style={{
		  display: "flex", justifyContent: "space-between", alignItems: "center",
		  padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.1)"
		}}>
		  <h3 style={{ margin: 0, fontSize: "18px", color: "white", fontWeight: 600 }}>Чат звонка</h3>
		  <button onClick={() => setIsOpen(false)} style={{
			background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: "24px",
			display: "flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px",
			borderRadius: "4px", transition: "background 0.2s"
		  }} onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"} onMouseOut={(e) => e.currentTarget.style.background = "none"}>
			×
		  </button>
		</div>
		
		<div ref={scrollRef} style={{
		  flexGrow: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "12px"
		}}>
		  {messages.length === 0 ? (
			<div style={{ color: "#777", margin: "auto", textAlign: "center", fontSize: "14px" }}>
			  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: "12px", opacity: 0.5 }}>
				<path d="M21 11.5C21 16.75 16.5 21 12 21C10.5 21 9 20.5 7.5 19.5L3 21L4.5 16.5C3.5 15 3 13.5 3 11.5C3 6.25 7.5 2 12 2C16.5 2 21 6.25 21 11.5Z" />
			  </svg><br/>
			  Здесь пока тихо...<br/>Напишите первое сообщение!
			</div>
		  ) : (
			messages.map((m) => {
			  const isMe = m.senderId === myId;
			  const displayInfo = isMe ? "Я" : m.senderName;
			  
			  return (
				<div key={m.id} style={{ 
				  alignSelf: isMe ? "flex-end" : "flex-start", maxWidth: "85%",
				  display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start"
				}}>
				  <span style={{ fontSize: "12px", color: isMe ? "#00d2ff" : "#00ff88", marginBottom: "4px", padding: "0 4px", fontWeight: "bold" }}>
					{displayInfo}
				  </span>
				  <div style={{ 
					background: isMe ? "#00d2ff" : "rgba(255,255,255,0.1)",
					color: isMe ? "#000" : "#fff",
					padding: "10px 14px", borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
					fontSize: "14px", lineHeight: "1.4", wordBreak: "break-word",
					boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
				  }}>
					{m.text}
				  </div>
				</div>
			  );
			})
		  )}
		</div>

		<form onSubmit={handleSend} style={{
		  padding: "16px", borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", gap: "8px",
		  backgroundColor: "rgba(20, 20, 24, 0.5)"
		}}>
		  <input 
			value={inputText} onChange={(e) => setInputText(e.target.value)}
			placeholder="Написать..."
			style={{
			  flexGrow: 1, padding: "12px 16px", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.2)",
			  background: "rgba(0,0,0,0.3)", color: "white", outline: "none", fontSize: "14px",
			  transition: "border 0.2s"
			}}
			onFocus={(e) => e.target.style.border = "1px solid #00d2ff"}
			onBlur={(e) => e.target.style.border = "1px solid rgba(255,255,255,0.2)"}
		  />
		  <button type="submit" disabled={!inputText.trim()} style={{
			width: "42px", height: "42px", borderRadius: "50%", cursor: inputText.trim() ? "pointer" : "default",
			backgroundColor: inputText.trim() ? "#00d2ff" : "rgba(255,255,255,0.1)", border: "none",
			display: "flex", alignItems: "center", justifyContent: "center", transition: "0.2s",
			color: inputText.trim() ? "#000" : "rgba(255,255,255,0.3)"
		  }}>
			<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			  <line x1="22" y1="2" x2="11" y2="13"></line>
			  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
			</svg>
		  </button>
		</form>
	  </div>
	</>
  );
};